import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type PLGEvent = 
  | 'visitor_timer_start'
  | 'login_gate_shown'
  | 'premium_gate_shown'
  | 'login_gate_clicked_signup'
  | 'login_gate_clicked_login'
  | 'premium_gate_clicked_upgrade'
  | 'premium_waitlist_signup'
  // Timer share events - WA/IG integration tracking
  | 'workout_share_initiated'
  | 'workout_share_whatsapp_direct'
  | 'workout_share_whatsapp_generic'
  | 'workout_share_instagram'
  // New PLG events for conversion tracking
  | 'first_round_completed'
  | 'first_workout_completed'
  | 'share_attempted'
  | 'share_completed'
  | 'returned_within_24h'
  | 'feature_discovered'
  | 'timer_auto_started'
  | 'preset_selected'
  | 'workout_completed'
  | 'celebration_shown'
  | 'post_value_gate_shown'
  | 'post_value_signup'
  // Sprint P0 v3 — vício / loop fechado
  | 'session_finished'
  | 'session_abandoned'
  | 'next_student_from_celebration'
  | 'accidental_tap'
  // Loop de sessão — funil honesto
  | 'preset_loaded'
  | 'session_started'
  | 'session_queued_offline'
  | 'session_save_failed'
  | 'schedule_completed'
  // Sprint Mestre Cobra — gates contextuais
  | 'contextual_gate_shown'
  | 'contextual_gate_cta_click'
  // Landing pública — funil lead magnet
  | 'landing_view'
  | 'landing_cta_timer'
  | 'landing_cta_whatsapp'
  | 'landing_sticky_cta'
  | 'landing_faq_open'
  | 'landing_chat_demo_toggle'
  | 'landing_scroll_depth'
  | 'post_value_whatsapp';

interface EventMetadata {
  feature?: string;
  source?: string;
  [key: string]: string | number | boolean | undefined;
}

// Generate or retrieve session ID for anonymous tracking
const getSessionId = (): string => {
  const key = 'plg_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
};

// Track total workouts completed in session for gate timing
const WORKOUTS_KEY = 'plg_workouts_completed';

export const getWorkoutsCompleted = (): number => {
  const stored = localStorage.getItem(WORKOUTS_KEY);
  return stored ? parseInt(stored, 10) : 0;
};

export const incrementWorkoutsCompleted = (): number => {
  const current = getWorkoutsCompleted();
  const next = current + 1;
  localStorage.setItem(WORKOUTS_KEY, next.toString());
  return next;
};

export const usePLGAnalytics = () => {
  const trackEvent = useCallback(async (event: PLGEvent, metadata?: EventMetadata) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const sessionId = getSessionId();
      
      // Log to console in development
      console.log('[PLG Analytics]', event, {
        user_id: user?.id || 'anonymous',
        session_id: sessionId,
        timestamp: new Date().toISOString(),
        ...metadata,
      });

      // Save to database
      const { error } = await supabase
        .from('analytics_events')
        .insert({
          event_type: event,
          user_id: user?.id || null,
          session_id: sessionId,
          metadata: metadata || {},
        });

      if (error) {
        console.error('[PLG Analytics] Error saving event:', error);
      }
    } catch (error) {
      console.error('[PLG Analytics] Error tracking event:', error);
    }
  }, []);

  return { trackEvent, getWorkoutsCompleted, incrementWorkoutsCompleted };
};

// Standalone function for components that can't use hooks
export const trackPLGEvent = async (event: PLGEvent, metadata?: EventMetadata) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const sessionId = getSessionId();
    
    console.log('[PLG Analytics]', event, {
      user_id: user?.id || 'anonymous',
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      ...metadata,
    });

    // Save to database
    const { error } = await supabase
      .from('analytics_events')
      .insert({
        event_type: event,
        user_id: user?.id || null,
        session_id: sessionId,
        metadata: metadata || {},
      });

    if (error) {
      console.error('[PLG Analytics] Error saving event:', error);
    }
  } catch (error) {
    console.error('[PLG Analytics] Error tracking event:', error);
  }
};
